import { useForm } from 'react-hook-form'

import { ApolloError } from '@apollo/client'
import { yupResolver } from '@hookform/resolvers/yup'

import { useResetPasswordRequestMutation } from '~/app/generated/graphql'
import { useMarketContext } from '~/components/SettingsProvider/MarketClientContext'
import { gt } from '~/lib/gt'

import { formatErrorMessages } from '../AuthModals/LoginModal/formatErrorMessages'
import { Button } from '../Button/Button'
import { FormInput } from '../FormInput/FormInput'
import { useToast } from '../Toast'

import styles from './ForgotPasswordModal.module.css'
import { validationSchema } from './ForgotPasswordModalUtils'

interface ForgotPasswordModalViewProps {
  onSuccess: () => void
}

const ForgotPasswordModalView = ({ onSuccess }: ForgotPasswordModalViewProps) => {
  const [resetPassword] = useResetPasswordRequestMutation()
  const toast = useToast()
  const { supportEmail } = useMarketContext()

  const handleErrors = (error: ApolloError) => {
    const errors = formatErrorMessages(error, { supportEmail })
    errors?.map(error =>
      toast?.add({
        message: error,
        type: 'error',
        containerSelector: '#ForgotPasswordToastContainer',
        size: 'small',
      })
    )
  }

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      email: '',
    },
    resolver: yupResolver(validationSchema),
  })

  const onSubmit = async (values: { email: string }) => {
    await resetPassword({
      variables: values,
      onCompleted: () => {
        onSuccess && onSuccess()
      },
      onError: error => handleErrors(error),
    })
  }

  return (
    <form className={styles.forgotPasswordModal} onSubmit={handleSubmit(onSubmit)} data-color-scheme='white'>
      <h3 className={styles.h3}>{gt.tp('ForgotPasswordModal', 'Forgot password')}</h3>
      <div id='ForgotPasswordToastContainer' className={styles.toastContainer} />
      <h6 className={styles.h6}>
        {gt.tp('ForgotPasswordModal', 'Enter the email address used for registration to receive a password reset link')}
      </h6>
      <div className={styles.inputContainer}>
        <FormInput
          {...register('email')}
          type={'email'}
          label={gt.tp('ForgotPasswordModal', 'Email*')}
          className={styles.input}
          errorMessage={errors.email?.message}
          defaultValue=''
        />
      </div>
      <Button className={styles.submitButton} type='submit' disabled={!isValid || isSubmitting}>
        {gt.tp('ForgotPasswordModal', 'Send a password reset link')}
      </Button>
    </form>
  )
}

export default ForgotPasswordModalView
